"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, Gauge, Layers, Pipette, ShieldAlert, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface ProblemCategory {
  id: string;
  name: string;
  tag: string;
  description: string;
  urgency: "Immediate" | "Critical" | "High";
  href: string;
  iconName: string;
}
const problemCategories: ProblemCategory[] = [{
  id: "burst-pipes",
  name: "Burst & Frozen Pipes",
  tag: "Flooding Risk",
  description: "Active wall, ceiling, or crawlspace leaks requiring rapid mainline shutoff and pipe repair.",
  urgency: "Immediate",
  href: "/problems/",
  iconName: "Droplets"
}, {
  id: "drain-clogs",
  name: "Severe Drain Blockages",
  tag: "Water Stoppage",
  description: "Multi-fixture backups, overflowing kitchen stacks, and standing water in tubs or basins.",
  urgency: "Critical",
  href: "/problems/",
  iconName: "Pipette"
}, {
  id: "sewer-backup",
  name: "Main Sewer Line Backups",
  tag: "Biohazard",
  description: "Hazardous raw sewage coming up through lowest drains, floor traps, or outdoor cleanouts.",
  urgency: "Immediate",
  href: "/problems/",
  iconName: "ShieldAlert"
}, {
  id: "water-heater",
  name: "Water Heater Failures",
  tag: "Tank & Tankless",
  description: "Ruptured tanks, sudden total loss of hot water, burning smells, or continuous pressure relief leaks.",
  urgency: "Critical",
  href: "/water-heater/",
  iconName: "Flame"
}, {
  id: "toilet-overflow",
  name: "Toilet Overflows & Leaks",
  tag: "Structural Risk",
  description: "Continuous bowl rising, fractured porcelain base seals, and unyielding waste line blocks.",
  urgency: "High",
  href: "/problems/",
  iconName: "Layers"
}, {
  id: "low-pressure",
  name: "Sudden Water Pressure Drop",
  tag: "Supply Issue",
  description: "Unexplained municipal meter drops, pinhole copper leaks, or PRV valve failures across the home.",
  urgency: "High",
  href: "/problems/",
  iconName: "Gauge"
}];
export default function Categories() {
  const shouldReduceMotion = useReducedMotion();
  const getUrgencyBadge = (urgency: ProblemCategory["urgency"]) => {
    switch (urgency) {
      case "Immediate":
        return <Badge variant="destructive" className="font-semibold tracking-wide uppercase text-[10px]">Immediate 24/7</Badge>;
      case "Critical":
        return <Badge className="bg-accent text-accent-foreground font-semibold tracking-wide uppercase text-[10px]">Critical</Badge>;
      case "High":
      default:
        return <Badge variant="secondary" className="font-semibold tracking-wide uppercase text-[10px]">Priority</Badge>;
    }
  };
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "Droplets":
        return <Droplets className="size-6 text-primary" aria-hidden="true" />;
      case "Pipette":
        return <Pipette className="size-6 text-primary" aria-hidden="true" />;
      case "ShieldAlert":
        return <ShieldAlert className="size-6 text-destructive" aria-hidden="true" />;
      case "Flame":
        return <Flame className="size-6 text-accent-foreground" aria-hidden="true" />;
      case "Layers":
        return <Layers className="size-6 text-primary" aria-hidden="true" />;
      case "Gauge":
      default:
        return <Gauge className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="relatedproblemcategories" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <AlertTriangle className="size-3.5 text-primary" aria-hidden="true" />
            <span>Emergency Diagnostics</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            Explore Common Plumbing Emergencies
          </h2>
          
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Quickly identify your plumbing failure below for immediate response steps, troubleshooting insights, and direct dispatch across Jonesboro, GA.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {problemCategories.map((cat, i) => <motion.div key={cat.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.35,
          delay: i * 0.05
        }} className="h-full flex flex-col">
              <Card className="h-full flex flex-col justify-between border-border bg-card hover:border-primary/40 hover:shadow-md transition-all duration-200">
                <CardContent className="p-6 md:p-8 flex flex-col h-full">
                  
                  {/* Top Bar inside Card */}
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <div className="p-3 rounded-lg bg-secondary/70 flex items-center justify-center">
                      {renderIcon(cat.iconName)}
                    </div>
                    {getUrgencyBadge(cat.urgency)}
                  </div>

                  {/* Tag and Title */}
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {cat.tag}
                  </span>
                  
                  <h3 className="text-xl font-default font-semibold text-foreground mt-1 mb-3">
                    {cat.name}
                  </h3>

                  {/* Body description */}
                  <p className="text-sm text-muted-foreground leading-relaxed flex-grow">
                    {cat.description}
                  </p>

                  {/* CTA Action */}
                  <div className="mt-6 pt-5 border-t border-border flex items-center justify-between">
                    <Button variant="link" asChild className="p-0 h-auto text-primary font-semibold group">
                      <Link to={cat.href} className="inline-flex items-center gap-1.5 text-sm">
                        <span>Troubleshooting guide</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                      </Link>
                    </Button>
                    <span className="text-xs font-mono text-muted-foreground">24/7 Available</span>
                  </div>

                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Banner */}
        <div className="mt-12 p-6 md:p-8 rounded-2xl bg-secondary/60 border border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-full bg-primary flex items-center justify-center shrink-0">
              <Wrench className="size-6 text-primary-foreground" aria-hidden="true" />
            </div>
            <div>
              <h4 className="text-base md:text-lg font-default font-semibold text-foreground">
                Experiencing an unlisted plumbing issue?
              </h4>
              <p className="text-sm text-muted-foreground">
                Our Jonesboro emergency technicians handle all residential and commercial plumbing failures.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/contact/">
                Get Rapid Dispatch
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}